import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../../../contexts/AuthContext';
import Layout from '../../../components/shared/Layout/Layout';
import Button from '../../../components/shared/Button/Button';
import { cssVar } from '../../../utils/theme';
import { readRef } from '../../../utils/promoterRef';
import CurpField from '../../../components/sales/CurpField';
import { PrivacyBox } from '../../../components/sales/sales.styles';
import { AttendeeRow } from '../../sales/Sell.styles';
import {
  PRIVACY_CHECKBOX_LABEL_SELF,
  privacyNoticeParagraphs,
} from '../../../content/privacyNoticeCurp';
import {
  FormGroup,
  FormLabel,
  FormInput,
  ErrorMessage,
} from '../../../components/shared/Form/Form.styles';
import {
  CheckoutContainer,
  CheckoutContent,
  BookingSummary,
  SummaryCard,
  ActivityInfo,
  BookingDetails,
  PaymentSection,
  PaymentCard,
  PriceBreakdown,
  TotalAmount,
  LoginPrompt,
  CardFieldWrap,
  SecureNote,
  PolicyBox,
} from './Checkout.styles';

// Stripe Elements vive en un iframe y no entiende var(--x): se leen los tokens al renderizar.
const getCardElementOptions = () => ({
  style: {
    base: {
      fontSize: '16px',
      fontFamily: cssVar('--font-brand', 'sans-serif'),
      color: cssVar('--text-primary', '#0F1B27'),
      '::placeholder': { color: cssVar('--text-muted', '#5B6B7A') },
    },
    invalid: { color: cssVar('--danger', '#D81E40') },
  },
  hidePostalCode: true,
});

// Mensajes del 400 de POST /api/reservations por asistentes / CURP (fase 3 y 4).
const ATTENDEE_CODES = ['curp_required', 'curp_invalid', 'curp_not_required', 'privacy_notice_required'];

const CheckoutForm = ({ activityId, bookingData, stripeEnabled }) => {
  const cardElementOptions = useMemo(getCardElementOptions, []);
  const navigate = useNavigate();
  const { user } = useAuth();
  const stripe = useStripe();
  const elements = useElements();

  const [guestData, setGuestData] = useState({ name: '', email: '', phone: '' });
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  // Atribución a un promotor (enlace ?ref=): con código se mandan SIEMPRE los
  // asistentes (un nombre por lugar). Si la actividad pide CURP, el backend lo
  // avisa con `curp_required` y entonces se piden CURP y aviso de privacidad.
  const promoterCode = useMemo(() => readRef(activityId), [activityId]);
  const seats = bookingData.quantity;
  const [curpMode, setCurpMode] = useState(() => Boolean(bookingData.requireCurp));
  const [attendeeNames, setAttendeeNames] = useState(() => Array.from({ length: seats }, () => ''));
  const [curpErrors, setCurpErrors] = useState({});
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const curpRefs = useRef([]);
  const attendeesRef = useRef(null);
  const needAttendees = Boolean(promoterCode) || curpMode;

  const handleGuestDataChange = (e) => {
    const { name, value } = e.target;
    setGuestData(prev => ({ ...prev, [name]: value }));
  };

  const customerInfo = user
    ? { name: user.name, email: user.email, phone: user.phone || '' }
    : guestData;

  const subtotal = bookingData.totalPrice;
  const serviceFee = subtotal * 0.05;
  const taxes = (subtotal + serviceFee) * 0.16;
  const total = subtotal + serviceFee + taxes;

  // El primer asistente es, por omisión, quien reserva.
  const nameOf = (i) => attendeeNames[i].trim() || (i === 0 ? (customerInfo.name || '').trim() : '');

  const clearCurps = () => {
    curpRefs.current.forEach((field) => field && field.clear && field.clear());
  };

  const showAttendeeError = (message) => {
    setError(message);
    setTimeout(() => {
      if (attendeesRef.current && attendeesRef.current.scrollIntoView) {
        attendeesRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setCurpErrors({});

    if (stripeEnabled && (!stripe || !elements)) return;

    let attendees;
    if (needAttendees) {
      attendees = [];
      for (let i = 0; i < seats; i += 1) {
        const name = nameOf(i);
        if (!name) {
          showAttendeeError(`Escribe el nombre del asistente #${i + 1}.`);
          return;
        }
        const entry = { name };
        if (curpMode) {
          const field = curpRefs.current[i];
          if (!field || !field.getValue()) {
            setCurpErrors({ [i]: 'Escribe el CURP de este asistente.' });
            showAttendeeError(`Escribe el CURP del asistente #${i + 1}.`);
            return;
          }
          if (!field.validate()) {
            showAttendeeError(`Revisa el CURP del asistente #${i + 1}.`);
            field.focus();
            return;
          }
          entry.curp = field.getValue();
        }
        attendees.push(entry);
      }
      if (curpMode && !privacyAccepted) {
        showAttendeeError('Acepta el aviso de privacidad para registrar el CURP.');
        return;
      }
    }

    setProcessing(true);

    try {
      // 1. Crear la reserva (retiene el cupo y crea el PaymentIntent)
      const response = await fetch('/api/reservations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user && { Authorization: `Bearer ${localStorage.getItem('token')}` }),
        },
        body: JSON.stringify({
          activityId,
          date: bookingData.date,
          time: bookingData.time,
          quantity: bookingData.quantity,
          customerInfo,
          ...(promoterCode && { promoterCode }),
          ...(attendees && { attendees }),
          ...(curpMode && { privacyNoticeAccepted: true }),
        }),
      });
      const result = await response.json();

      if (!result.success) {
        // `promoterApplied: false` no llega aquí (es de una reserva creada): el cliente nunca se entera.
        if (ATTENDEE_CODES.includes(result.code)) {
          const match = /#(\d+)/.exec(result.error || '');
          if (result.code === 'curp_required' && !curpMode) {
            // La actividad pide CURP: se muestran los campos y el aviso de privacidad.
            setCurpMode(true);
            setPrivacyAccepted(false);
            showAttendeeError('Esta actividad pide el nombre y el CURP de cada asistente. Escríbelos, acepta el aviso de privacidad y vuelve a pagar.');
          } else {
            if (result.code === 'curp_not_required') setCurpMode(false);
            if (match && (result.code === 'curp_required' || result.code === 'curp_invalid')) {
              setCurpErrors({ [Number(match[1]) - 1]: result.error });
            }
            showAttendeeError(result.error || 'Revisa los datos de los asistentes.');
          }
        } else {
          setError(result.error || 'No se pudo crear la reserva.');
        }
        return;
      }

      // Los CURP ya no hacen falta en pantalla.
      clearCurps();

      const reservation = result.reservation;

      // Modo prueba sin Stripe: el backend entrega el QR de inmediato
      if (!result.clientSecret) {
        navigate('/booking-confirmed', {
          state: { reservationData: reservation, qrCode: result.qrCode },
        });
        return;
      }

      // 2. Confirmar la tarjeta con Stripe (los datos nunca tocan nuestro servidor)
      const cardResult = await stripe.confirmCardPayment(result.clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: customerInfo.name,
            email: customerInfo.email,
          },
        },
      });

      if (cardResult.error) {
        setError(cardResult.error.message || 'El pago fue rechazado. Verifica tu tarjeta.');
        return;
      }

      // 3. Verificar en el backend que el pago quedó retenido y obtener el QR
      const confirmResponse = await fetch(`/api/reservations/${reservation.id}/confirm-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const confirmResult = await confirmResponse.json();

      if (!confirmResult.success) {
        setError(confirmResult.error || 'No pudimos verificar tu pago. Contacta a soporte.');
        return;
      }

      navigate('/booking-confirmed', {
        state: {
          reservationData: confirmResult.reservation,
          qrCode: confirmResult.qrCode,
        },
      });
    } catch (err) {
      setError('Error de conexión. Intenta nuevamente.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {!user && (
        <>
          <h3>Información del cliente</h3>
          <FormGroup>
            <FormLabel>Nombre completo</FormLabel>
            <FormInput
              type="text"
              name="name"
              value={guestData.name}
              onChange={handleGuestDataChange}
              placeholder="Como aparecerá en tu reserva"
              required
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>Email</FormLabel>
            <FormInput
              type="email"
              name="email"
              value={guestData.email}
              onChange={handleGuestDataChange}
              placeholder="Recibirás tu QR en este correo"
              required
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>Teléfono</FormLabel>
            <FormInput
              type="tel"
              name="phone"
              value={guestData.phone}
              onChange={handleGuestDataChange}
              placeholder="10 dígitos"
              required
            />
          </FormGroup>
        </>
      )}

      {needAttendees && (
        <div ref={attendeesRef}>
          <h3>Asistentes</h3>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.5 }}>
            Un nombre por lugar ({seats} {seats === 1 ? 'lugar' : 'lugares'}).
            {curpMode && ' Esta actividad también pide el CURP de cada asistente: se muestra oculto y se guarda cifrado.'}
          </p>
          {attendeeNames.map((name, i) => (
            <AttendeeRow key={i}>
              <legend>Asistente {i + 1}</legend>
              <FormGroup style={{ marginBottom: 0 }}>
                <FormLabel htmlFor={`co-attendee-${i}`}>Nombre completo</FormLabel>
                <FormInput
                  id={`co-attendee-${i}`}
                  value={name}
                  maxLength={100}
                  autoComplete="off"
                  placeholder={i === 0 ? (customerInfo.name || 'Nombre de quien reserva') : ''}
                  onChange={(ev) => {
                    const value = ev.target.value;
                    setAttendeeNames((current) => current.map((n, idx) => (idx === i ? value : n)));
                  }}
                />
              </FormGroup>
              {curpMode && (
                <CurpField
                  id={`co-curp-${i}`}
                  label="CURP"
                  ref={(el) => { curpRefs.current[i] = el; }}
                  serverError={curpErrors[i]}
                />
              )}
            </AttendeeRow>
          ))}

          {curpMode && (
            <PrivacyBox>
              <h3>Aviso de privacidad</h3>
              <div className="notice-text" tabIndex={0} role="region" aria-label="Texto del aviso de privacidad">
                {privacyNoticeParagraphs().map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
              <label className="accept">
                <input
                  type="checkbox"
                  checked={privacyAccepted}
                  onChange={(ev) => setPrivacyAccepted(ev.target.checked)}
                />
                <span>{PRIVACY_CHECKBOX_LABEL_SELF}</span>
              </label>
            </PrivacyBox>
          )}
        </div>
      )}

      {stripeEnabled && (
        <>
          <h3>Pago con tarjeta</h3>
          <CardFieldWrap>
            <CardElement options={cardElementOptions} />
          </CardFieldWrap>
          <SecureNote>
            🔒 Pago procesado por Stripe. Tus datos de tarjeta nunca pasan por
            nuestros servidores.
          </SecureNote>
        </>
      )}

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <PolicyBox>
        <strong>Tu dinero, protegido</strong>
        <p>El pago se retiene de forma segura y solo se libera a la empresa cuando presentas tu código QR en el lugar.</p>
        <p>Si la actividad no se realiza, el reembolso es automático.</p>
        <p>Puedes cancelar hasta 24 horas antes con reembolso completo.</p>
      </PolicyBox>

      <Button
        type="submit"
        fullWidth
        disabled={processing || (stripeEnabled && !stripe)}
        style={{ marginTop: '20px' }}
      >
        {processing
          ? 'Procesando pago...'
          : `Pagar $${total.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
      </Button>
    </form>
  );
};

const Checkout = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [bookingData, setBookingData] = useState(null);
  const [paymentsConfig, setPaymentsConfig] = useState(null);

  useEffect(() => {
    if (location.state) {
      setBookingData(location.state);
    } else {
      navigate(`/activity/${id}`);
    }
  }, [location.state, id, navigate]);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await fetch('/api/payments/config');
        const data = await response.json();
        setPaymentsConfig(data);
      } catch {
        setPaymentsConfig({ stripeEnabled: false });
      }
    };
    loadConfig();
  }, []);

  const stripePromise = useMemo(() => {
    if (paymentsConfig?.stripeEnabled && paymentsConfig.publishableKey) {
      return loadStripe(paymentsConfig.publishableKey);
    }
    return null;
  }, [paymentsConfig]);

  if (!bookingData || !paymentsConfig) {
    return (
      <Layout>
        <div className="loading">Cargando información de reserva...</div>
      </Layout>
    );
  }

  const subtotal = bookingData.totalPrice;
  const serviceFee = subtotal * 0.05;
  const taxes = (subtotal + serviceFee) * 0.16;
  const total = subtotal + serviceFee + taxes;
  const fmt = (n) => n.toLocaleString(undefined, { maximumFractionDigits: 2 });

  const form = (
    <CheckoutForm
      activityId={id}
      bookingData={bookingData}
      stripeEnabled={Boolean(stripePromise)}
    />
  );

  return (
    <Layout>
      <CheckoutContainer>
        <CheckoutContent>
          <BookingSummary>
            <SummaryCard>
              <h2>Resumen de tu reserva</h2>

              <ActivityInfo>
                <h3>{bookingData.activityTitle || 'Actividad'}</h3>
                <p>📍 {bookingData.activityLocation || 'Ubicación por confirmar'}</p>
              </ActivityInfo>

              <BookingDetails>
                <div className="detail-row">
                  <span>Fecha</span>
                  <span>{new Date(`${bookingData.date}T12:00:00`).toLocaleDateString()}</span>
                </div>
                <div className="detail-row">
                  <span>Hora</span>
                  <span>{bookingData.time}</span>
                </div>
                <div className="detail-row">
                  <span>Personas</span>
                  <span>{bookingData.quantity}</span>
                </div>
              </BookingDetails>

              <PriceBreakdown>
                <div className="price-row">
                  <span>Subtotal</span>
                  <span>${fmt(subtotal)}</span>
                </div>
                <div className="price-row">
                  <span>Tarifa de servicio (5%)</span>
                  <span>${fmt(serviceFee)}</span>
                </div>
                <div className="price-row">
                  <span>IVA (16%)</span>
                  <span>${fmt(taxes)}</span>
                </div>
                <div className="divider"></div>
                <TotalAmount>
                  <span>Total</span>
                  <span>${fmt(total)}</span>
                </TotalAmount>
              </PriceBreakdown>
            </SummaryCard>
          </BookingSummary>

          <PaymentSection>
            <PaymentCard>
              <h2>Completar reserva</h2>

              {!user && (
                <LoginPrompt>
                  <p>
                    ¿Ya tienes cuenta?{' '}
                    <button type="button" onClick={() => navigate('/login')}>
                      Inicia sesión
                    </button>
                  </p>
                  <div className="divider">o continúa como invitado</div>
                </LoginPrompt>
              )}

              {/* Siempre dentro de <Elements>: useStripe() truena fuera de él. Sin Stripe
                  (pruebas locales) `stripe` es null y el formulario no pide tarjeta. */}
              <Elements stripe={stripePromise || null}>{form}</Elements>
            </PaymentCard>
          </PaymentSection>
        </CheckoutContent>
      </CheckoutContainer>
    </Layout>
  );
};

export default Checkout;
